import { createContext, useState, useCallback } from "react";

// Load environment variables
const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const USERNAME = process.env.REACT_APP_USERNAME;
const PASSWORD = process.env.REACT_APP_PASSWORD;

// Create YachtContext
export const YachtContext = createContext();

// Helper function to get authorization headers
const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: "Basic " + btoa(`${USERNAME}:${PASSWORD}`),
});

export const YachtProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [categoryYachts, setCategoryYachts] = useState({});
  const [allYachts, setAllYachts] = useState([]);
  const [yacht, setYacht] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [yachtLoading, setYachtLoading] = useState(false);
  const [yachtError, setYachtError] = useState(null);

  // Fetch all categories & yachts
  const fetchCategoriesAndYachts = useCallback(async () => {
    setLoading(true);
    try {
      console.log("Fetching categories...");

      const categoryResponse = await fetch(`${SERVER_URL}api/categories`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!categoryResponse.ok) throw new Error("Failed to fetch categories");

      const categoryData = await categoryResponse.json();
      console.log("Categories fetched:", categoryData);

      setCategories(categoryData); // ✅ Set categories

      // Fetch yachts for each category
      const yachtsPromises = categoryData.map((category) =>
        fetch(`${SERVER_URL}api/categories/${category.category_url}`, {
          method: "GET",
          headers: getAuthHeaders(),
        })
          .then((response) => response.json())
          .catch((err) => {
            console.error(`Error fetching yachts for category ${category.category_url}:`, err);
            return [];
          })
      );

      const yachtsData = await Promise.all(yachtsPromises);
      console.log("Yachts by category fetched:", yachtsData);

      // Map yachts to categories
      const yachtsByCategory = categoryData.reduce((acc, category, index) => {
        acc[category.category_url] = yachtsData[index];
        return acc;
      }, {});

      setCategoryYachts(yachtsByCategory); // ✅ Set yachts by category
    } catch (err) {
      console.error("Error fetching categories/yachts:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);


  // Fetch all yachts
  const fetchAllYachts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${SERVER_URL}api/yachts`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error("Failed to fetch yachts");

      const data = await response.json();
      setAllYachts(data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching all yachts:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch yacht details by ID
  const fetchYachtDetails = useCallback(async (id) => {
    setYachtLoading(true);
    try {
      const response = await fetch(`${SERVER_URL}api/yacht/${id}`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error("Yacht not found");

      const data = await response.json();
      setYacht(data);
    } catch (err) {
      setYachtError("Error fetching yacht details.");
      console.error(err);
    } finally {
      setYachtLoading(false);
    }
  }, []);

  return (
    <YachtContext.Provider
      value={{
        fetchCategoriesAndYachts,
        categories,
        categoryYachts,
        fetchAllYachts,
        allYachts,
        fetchYachtDetails,
        yacht,
        yachtError,
        yachtLoading,
        loading,
        error,
      }}
    >
      {children}
    </YachtContext.Provider>
  );
};
